// From https://github.com/tom-alexander/regression-js
// @author Tom Alexander

type XYPair = [number, number];

/**
 * Determine the coefficient of determination (r^2) of a fit from the observations
 * and predictions.
 *
 * @param {Array<Array<number>>} data - Pairs of observed x-y values [ [0, 0.1], [1, 1.9], [2, 4.1] ]
 * @param {Array<number>} results - Pairs of observed predicted x-y values [ [0, 0], [1, 2], [2, 4] ]
 *
 * @return {number} - The r^2 value, or NaN if one cannot be calculated.
 */
function determinationCoefficient(data: XYPair[], results: XYPair[]) {
  const predictions = [];
  const observations = [];

  data.forEach((d, i) => {
    if (d[1] !== null) {
      observations.push(d);
      predictions.push(results[i]);
    }
  });

  const sum = observations.reduce((a, observation) => a + observation[1], 0);
  const mean = sum / observations.length;

  const ssyy = observations.reduce((a, observation) => {
    const difference = observation[1] - mean;
    return a + difference * difference;
  }, 0);

  const sse = observations.reduce((accum, observation, index) => {
    const prediction = predictions[index];
    const residual = observation[1] - prediction[1];
    return accum + residual * residual;
  }, 0);

  return 1 - sse / ssyy;
}

/**
 * Round a number to a precision, specificed in number of decimal places
 *
 * @param {number} number - The number to round
 * @param {number} precision - The number of decimal places to round to:
 *                             > 0 means decimals, < 0 means powers of 10
 *
 *
 * @return {number} - The number, rounded
 */
function round(number: number, precision: number) {
  const factor = 10 ** precision;
  return Math.round(number * factor) / factor;
}

/**
 * Make a linear regression of the supplied data
 * @param data An array of [x,y] tuples
 * @param precision Digits of precision in rounding, defaults 2
 */
export function linearRegression(data: XYPair[], precision: number = 2) {
  const sum = [0, 0, 0, 0, 0];
  let len = 0;

  for (let n = 0; n < data.length; n++) {
    if (data[n][1] !== null) {
      len++;
      sum[0] += data[n][0];
      sum[1] += data[n][1];
      sum[2] += data[n][0] * data[n][0];
      sum[3] += data[n][0] * data[n][1];
      sum[4] += data[n][1] * data[n][1];
    }
  }

  const run = len * sum[2] - sum[0] * sum[0];
  const rise = len * sum[3] - sum[0] * sum[1];
  const gradient = run === 0 ? 0 : round(rise / run, precision);
  const intercept = round(sum[1] / len - (gradient * sum[0]) / len, precision);

  const predict = (x: number): XYPair => [
    round(x, precision),
    round(gradient * x + intercept, precision),
  ];

  const points = data.map(([x]) => predict(x));

  return {
    points,
    predict,
    gradient,
    intercept,
    r2: round(determinationCoefficient(data, points), precision),
    string: intercept === 0 ? `y = ${gradient}x` : `y = ${gradient}x + ${intercept}`,
  };
}
